import React, { useMemo } from 'react';

import HeadingTag from '../../../display/components/HeadingTag';
import useId from '../../../utils/hooks/use-id';
import useFetch from '../../../api/hooks/use-fetch';
import createFetcher from '../utils/create-fetcher';
import groupStateOfficials from '../utils/group-state-officials';
import formatBasicTable from '../utils/format-basic-table';
import { HEADSHOT_PATH, SECTION_HEADING_CLASSES } from '../constants';
import Loader from './Loader';
import ErrorText from './ErrorText';
import BasicTable from './BasicTable';
import Card from './Card';

import { EGovBodyFilter } from '../../../types';
import { IFetcherResponse } from '../types';

interface IStateProps {
  scrollTarget: HTMLElement;
}

const State: React.FC<IStateProps> = ({ scrollTarget }) => {
  const stateOfficialsId = useId();
  const supremeCourtId = useId();
  const criminalCourtId = useId();
  const edBoardId = useId();
  const railroadId = useId();
  const { data, isPending, didError } = useFetch<IFetcherResponse>({
    element: scrollTarget,
    fetchData: createFetcher(EGovBodyFilter.State),
  });

  const rows = useMemo(() => {
    if (!data) {
      return null;
    }

    const formattedData = formatBasicTable(data);
    const { supremeCourtRows, criminalCourtRows, edBoardRows, railroadRows } =
      groupStateOfficials(formattedData);

    return {
      supremeCourtRows,
      criminalCourtRows,
      edBoardRows,
      railroadRows,
    };
  }, [data]);

  if (isPending) {
    return <Loader />;
  }

  return (
    <>
      <section aria-labelledby={stateOfficialsId} className="has-xl-btm-marg">
        <HeadingTag level={3} id={stateOfficialsId} display={{ srOnly: true }}>
          State officials
        </HeadingTag>
        <div className="l-directory-card-grid has-page-padding">
          <Card
            imageUrl={`${HEADSHOT_PATH}abbott.png`}
            name="Greg Abbott"
            party="republican"
            title="Governor"
            url="/directory/greg-abbott/"
          />
          <Card
            imageUrl={`${HEADSHOT_PATH}patrick.png`}
            name="Dan Patrick"
            party="republican"
            title="Lt. Governor"
            url="/directory/dan-patrick/"
          />
          <Card
            imageUrl={`${HEADSHOT_PATH}hegar.png`}
            name="Glenn Hegar"
            party="republican"
            title="Comptroller"
            url="/directory/glenn-hegar/"
          />
          <Card
            imageUrl={`${HEADSHOT_PATH}paxton.png`}
            name="Ken Paxton"
            party="republican"
            title="Attorney General"
            url="/directory/ken-paxton/"
          />
          <Card
            imageUrl={`${HEADSHOT_PATH}buckingham.png`}
            name="Dawn Buckingham"
            party="republican"
            title="Land Commissioner"
            url="/directory/dawn-buckingham/"
          />
          <Card
            imageUrl={`${HEADSHOT_PATH}miller.png`}
            name="Sid Miller"
            party="republican"
            title="Agriculture Commissioner"
            url="/directory/sid-miller/"
          />
        </div>
      </section>
      {rows && (
        <div className="l-col-grid l-col-grid--xxxl-gap l-col-grid--1x1-until-bp-m t-size-s">
          <section aria-labelledby={supremeCourtId} className="has-b-btm-marg">
            <HeadingTag
              level={3}
              id={supremeCourtId}
              display={{ cssClasses: SECTION_HEADING_CLASSES }}
            >
              Supreme Court of Texas
            </HeadingTag>
            <BasicTable data={rows.supremeCourtRows} />
          </section>
          <section aria-labelledby={criminalCourtId} className="has-b-btm-marg">
            <HeadingTag
              level={3}
              id={criminalCourtId}
              display={{ cssClasses: SECTION_HEADING_CLASSES }}
            >
              Texas Court of Criminal Appeals
            </HeadingTag>
            <BasicTable data={rows.criminalCourtRows} />
          </section>
          <section aria-labelledby={edBoardId} className="has-b-btm-marg">
            <HeadingTag
              level={3}
              id={edBoardId}
              display={{ cssClasses: SECTION_HEADING_CLASSES }}
            >
              State Board of Education
            </HeadingTag>
            <BasicTable data={rows.edBoardRows} />
          </section>
          <section aria-labelledby={railroadId} className="has-b-btm-marg">
            <HeadingTag
              level={3}
              id={railroadId}
              display={{ cssClasses: SECTION_HEADING_CLASSES }}
            >
              Texas Railroad Commission
            </HeadingTag>
            <BasicTable data={rows.railroadRows} />
          </section>
        </div>
      )}
      {didError && <ErrorText />}
    </>
  );
};

export default State;
